import { Stack } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Toast from 'apollo-react/components/Toast';
import { AiSparkles } from 'apollo-react-icons';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { removeAllToasts, removeToast } from '../actions/ToastActions';
import { TOAST_TYPE } from '../constants/NotificationConstants';
import { useTranslation } from '../custom-hooks/useTranslation';
import { getUITA } from '../utils/commonUtils';
import * as domUtils from '../utils/domUtils';
import { getJsonFreeStorageItem } from '../utils/storageUtils';
import { isMobileDevice } from '../utils/windowDimensionsUtils';

const useStyles = makeStyles({
  toast: {
    zIndex: 4300,
    position: 'unset !important',
  },
  toastWithActionButton: {
    '& [class*="MuiSnackbarContent-root"]': {
      maxWidth: '520px !important',
      '& [class*="MuiSnackbarContent-message"] p': {
        paddingRight: '140px !important',
        cursor: 'pointer !important',
      },
      '& [class*="MuiSnackbarContent-action"] button:first-of-type': {
        maxWidth: '100px !important',
        whiteSpace: 'normal !important',
        margin: '5px !important',
      },
    },
  },
});

const ToastNotifications = () => {
  const { pathname, search } = useLocation();
  const translateString = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();

  const toasts = useSelector((state) => state.toasts);
  const panelConfig = useSelector((state) => state.panelLayout.config);
  const isTokenExpired = useSelector((state) => state.auth?.isTokenExpired);

  const [loadingToastId, setLoadingToastId] = useState(null);

  useEffect(() => {
    dispatch(removeAllToasts());
    setLoadingToastId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  useEffect(() => {
    if (isTokenExpired) {
      dispatch(removeAllToasts());
      setLoadingToastId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTokenExpired]);

  const handleClose = (e, id) => {
    if (!loadingToastId) {
      e && e.stopPropagation();
      dispatch(removeToast(id));
    }
  };

  const handleActionClick = (e, id, skipToastKey) => {
    e.stopPropagation();

    if (skipToastKey) {
      const skipToastNotificationKeys = getJsonFreeStorageItem('skipToastNotificationKeys') || [];

      localStorage.setItem(
        'skipToastNotificationKeys',
        JSON.stringify([...skipToastNotificationKeys, skipToastKey])
      );
    }

    handleClose(e, id);
  };

  const handleToastClick = async (navInfo, id) => {
    if (navInfo && !loadingToastId) {
      setLoadingToastId(id);
      await domUtils.handleToastNavigation(navInfo, panelConfig);

      dispatch(removeToast(id));
      setLoadingToastId(null);
    }
  };

  const isCurrentToastLoading = (id) => loadingToastId === id;
  const hasActionButton = (actionButtonText, id) =>
    !!actionButtonText && !isCurrentToastLoading(id);
  const getMessage = (id, message) =>
    isCurrentToastLoading(id) ? translateString('Opening the panel...') : translateString(message);

  const anchorPosition = { vertical: 'bottom', horizontal: 'right' };

  return (
    <Stack
      sx={{ position: 'absolute', bottom: 24, right: isMobileDevice() ? 0 : 24, alignItems: 'end' }}
      spacing={1}
    >
      {toasts.map(
        ({
          id,
          message,
          autoHideDuration,
          type,
          variant,
          actionButtonText,
          navInfo,
          skipToastKey,
        }) => (
          <Toast
            open
            key={id}
            variant={variant}
            anchorOrigin={anchorPosition}
            onClose={(e) => handleClose(e, id)}
            message={getMessage(id, message)}
            autoHideDuration={autoHideDuration || null}
            icon={type === TOAST_TYPE.AI ? AiSparkles : null}
            showActionButton={hasActionButton(actionButtonText, id)}
            actionButtonText={translateString(actionButtonText) || ''}
            onActionClick={(e) => handleActionClick(e, id, skipToastKey)}
            onClick={() => handleToastClick(navInfo, id)}
            className={classNames(classes.toast, {
              [classes.toastWithActionButton]: hasActionButton(actionButtonText, id),
            })}
            ui-ta={getUITA('ToastNotification')}
          />
        )
      )}
    </Stack>
  );
};

export default ToastNotifications;
